import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import type { BiddingScheduleDayDto, BiddingScheduleUpsertRequest } from '../types/BiddingScheduleTypes';
import type { ActiveListingCategory } from '../types/ActiveListingCategory';
import toast from 'react-hot-toast';

//  直近の未終了の通常入札の入札日を取得する
export const useGetBiddingDateForApplyQuery = (loginId?: string) => {
  const api = useApiClient();

  return useQuery<number, Error>({
    // loginId を混ぜることで、「Aさん用のキャッシュ」「Bさん用のキャッシュ」と記憶領域を分ける
    queryKey: ['biddingDateForApply', loginId],
    
    queryFn: async () => {
      // api.get を使うことで、認証トークンなどが自動で付与される
      const response = await api.get('/Calendar/GetBiddingDateForApply');
      return response.data;
    },

    // ログインIDが確定してからAPIを叩くように制御
    enabled: !!loginId,
    // 🔒 D-0141: 通常入札に加え入札会も比較対象になったことで、セッション中に「最も近い日付」が
    //   入れ替わる可能性が増えた。KB-014と同型の「未設定の時だけ埋める」画面側ガードが、
    //   古いキャッシュ値を先に拾って確定してしまわないよう、再マウント時は必ず最新値を待つ。
    staleTime: 0,
    gcTime: 0,
  });
};

export const useGetBiddingDateByPrefectureForApplyQuery = (
  prefectureType: number,
  loginId?: string,
  enabled: boolean = true,
) => {
  const api = useApiClient();

  return useQuery<number, Error>({
    queryKey: ['biddingDateByPrefectureApply', loginId, prefectureType],

    queryFn: async () => {
      const response = await api.get('/Calendar/GetBiddingDateByPrefectureForApply', {
        params: { prefectureType }
      });
      return response.data;
    },

    enabled,
  });
};

// 直近の未終了の入札会の入札日を取得する（入札会専用・GetBiddingDateByPrefectureForApplyQueryの姉妹フック）
export const useGetSaleBiddingDateForApplyQuery = (
  loginId?: string,
  enabled: boolean = true,
) => {
  const api = useApiClient();

  return useQuery<number, Error>({
    queryKey: ['saleBiddingDateForApply', loginId],

    queryFn: async () => {
      const response = await api.get('/Calendar/GetSaleBiddingDateForApply');
      return response.data;
    },

    enabled,
  });
};

// 現在開催中、または最も近い将来に開催される出品区分（通常入札-県内／通常入札-県外／入札会）を取得する
// 🔒 現時点では「一覧から入札」画面・「入札状況確認」画面が使う集約API（特定画面専用の設計ではなく、将来的にさらに他画面が使う可能性もある）
export const useGetActiveListingCategoryForApplyQuery = (
  loginId?: string,
  enabled: boolean = true,
) => {
  const api = useApiClient();

  return useQuery<ActiveListingCategory, Error>({
    queryKey: ['activeListingCategoryForApply', loginId],

    queryFn: async () => {
      const response = await api.get('/Calendar/GetActiveListingCategoryForApply');
      return response.data;
    },

    enabled: enabled && !!loginId,
    // 🔒 画面を開くたびに必ず「今の」開催状況を反映させたいため、キャッシュを即座に破棄する
    //   （staleTime:0だけだと再マウント時に古いキャッシュ値が一瞬先に返り、初期化effectの
    //   「未設定の時だけ埋める」ガードに阻まれて新しい値が反映されない＝useBiddingReportQuery等と同型の対処）
    staleTime: 0,
    gcTime: 0,
  });
};

// 直近の終了済の通常入札の入札日を取得する
export const useGetBiddingDateForResultQuery = (loginId?: string) => {
  const api = useApiClient();

  return useQuery<number, Error>({
    queryKey: ['biddingDateForResult', loginId],
    
    queryFn: async () => {
      const response = await api.get('/Calendar/GetBiddingDateForResult');
      return response.data;
    },

    // ログインIDが確定してからAPIを叩くように制御
    enabled: !!loginId,
    // 🔒 D-0141: GetBiddingDateForApply側と同じ理由でキャッシュを即座に破棄する（KB-014と同型対策）。
    staleTime: 0,
    gcTime: 0,
  });
};

//---------------------------------------------------------
// 入札スケジュール管理のAPIフック定義
//---------------------------------------------------------
/**
 * useGetMonthSchedulesQuery (取得用フック)
 * 1ヶ月分の入札スケジュール情報を一括取得する
 */
export const useGetMonthSchedulesQuery = (
  yearMonth: number, 
  category: number,
  options?: { enabled?: boolean } 
) => {
  // api インスタンスを取得。この内部で「トークンの付与」が行われる。
  const api = useApiClient();

  return useQuery<Record<string, BiddingScheduleDayDto>, Error>({
    queryKey: ['monthSchedules', yearMonth, category],
    queryFn: async () => {
      // ここでリクエストを投げる際、Authorizationヘッダーが空だとサーバーが401を返す。
      const response = await api.get('/Calendar/GetMonthSchedules', {
        params: { yearMonth, category }
      });
      return response.data;
    },

    // 認証ガードのロジック
    // 目的：不完全な状態（未ログインやパラメータ不足）でAPIを叩かないようにする。
    // 1. (options?.enabled ?? true)
    //    => 呼び出し元（Page）から「currentUserがいる時だけ」という条件を受け取る。
    // 2. !!yearMonth && !!category
    //    => パラメータが揃っていない初期レンダリング時の暴発を防ぐ。
    enabled: (options?.enabled ?? true) && !!yearMonth && !!category,
  });
};

/**
 * useUpsertSchedulesMutation (保存用フック)
 * スケジュール設定を一括保存する
 * * 実行タイミング（mutate）がユーザー操作に依存するため、
 * 基本的に「画面が表示されている＝認証済み」という前提だが、
 * 内部の api インスタンスが常に最新のトークンを使用することが重要。
 */
export const useUpsertSchedulesMutation = () => {
  const ERROR_MESSAGE = "登録に失敗しました。"; // エラー時メッセージ
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    // mutationFn は「保存ボタン」を押した時に初めて実行される
    mutationFn: async (payload: BiddingScheduleUpsertRequest) => {
      const response = await api.post('/Calendar/UpsertSchedules', payload);
      return response.data;
    },
    onSuccess: () => {
      // 保存に成功したら、古い月間データのキャッシュを無効化（リフレッシュ）する。
      // これにより、画面上の「実績時刻」などが最新状態に更新される。
      queryClient.invalidateQueries({ queryKey: ['monthSchedules'] });
    },

    onError: (error: any) => 
    {
      // サーバーから返ってきたエラーメッセージ（BadRequestの中身）を取得
      const serverMessage = error.response?.data;
      // サーバーからのメッセージが文字列であればそれを表示し、なければデフォルトを表示
      const message = typeof serverMessage === 'string' ? serverMessage : ERROR_MESSAGE;
      //トーストを出す
      toast.error(message, 
      {duration: 8000,style: {whiteSpace: 'pre-line', fontSize: '14px', lineHeight: '1.5',}, });
    },
  });
};